import { useCallback, useState } from 'react';

/**
 * A custom hook for managing the visibility state of various UI dialogs
 * and modals in a sensor dashboard or charting context.
 *
 * This hook centralizes dialog/modal control logic — especially for components like:
 * - Interval selection dialogs
 * - Threshold editors
 * - Export and statistics views
 * - Performance analysis
 *
 * It also tracks the currently active section, which can be used to scope modal context.
 *
 * @returns An object containing:
 * - Dialog visibility flags
 * - The currently active section
 * - Setters for manual control
 * - Convenience handlers for common UI triggers
 */
export function useDialogState() {
  // Controls visibility of the interval selection dialog
  const [showIntervalDialog, setShowIntervalDialog] = useState(false);

  // Controls visibility of the statistics summary modal
  const [showStatistics, setShowStatistics] = useState(false);

  // Controls visibility of the export dialog
  const [showExportDialog, setShowExportDialog] = useState(false);

  // Controls visibility of the performance breakdown dialog
  const [showPerformanceDialog, setShowPerformanceDialog] = useState(false);

  // Controls whether the threshold editor UI is currently visible
  const [showThresholdEditor, setShowThresholdEditor] = useState(true);

  // Stores the currently selected section (used for contextual dialogs)
  const [currentSection, setCurrentSection] = useState('');

  // Stores the variant for interval lines ('harmonics' or 'carrier')
  const [intervalVariant, setIntervalVariant] = useState<'harmonics' | 'carrier'>('harmonics');

  /**
   * Opens the interval dialog and sets the current section.
   * Used when the interval dialog needs section-specific context.
   */
  const openIntervalDialog = (section: string, variant: 'harmonics' | 'carrier' = 'harmonics') => {
    setCurrentSection(section);
    setIntervalVariant(variant);
    setShowIntervalDialog(true);
  };

  /**
   * Callback version of `openIntervalDialog`, useful for stable handler props.
   */
  const handleShowIntervalDialog = useCallback((section: string, variant: 'harmonics' | 'carrier' = 'harmonics') => {
    setCurrentSection(section);
    setIntervalVariant(variant);
    setShowIntervalDialog(true);
  }, []);

  /**
   * Opens the threshold editor (can be scoped via chartType or sensorName externally).
   */
  const handleEditThresholds = useCallback((chartType: string, sensorName?: string) => {
    setShowThresholdEditor(true);
  }, []);

  return {
    // state variables
    showIntervalDialog,
    showStatistics,
    showExportDialog,
    showPerformanceDialog,
    showThresholdEditor,
    currentSection,
    intervalVariant,

    // setters (for controlled usage or two-way bindings)
    setShowIntervalDialog,
    setShowStatistics,
    setShowExportDialog,
    setShowPerformanceDialog,
    setShowThresholdEditor,
    setCurrentSection,
    setIntervalVariant,

    // handlers (pre-wrapped callbacks for use in UI interactions)
    handleShowIntervalDialog,
    handleEditThresholds,
  };
}
